// frontend/src/pages/admin/GuestList.tsx
import React, { useState, useEffect, FormEvent } from 'react';
import { useCouple } from '../../contexts/CoupleContext';
import AdminLayout from '../../components/layouts/AdminLayout';
import Button from '../../components/common/Button';
import Alert from '../../components/common/Alert';
import Loader from '../../components/common/Loader';
import Modal from '../../components/common/Modal';
import Input from '../../components/common/Input';
import Textarea from '../../components/common/Textarea';
import { Users, Plus, Edit2, Trash2, Mail, Phone, Check, X } from 'react-feather';
import api from '../../services/api';

// Define RSVPStatus to match backend AttendanceStatus (lowercase)
export enum RSVPStatus {
  ATTENDING = 'attending',
  DECLINED = 'declined',
  PENDING = 'pending',
  TENTATIVE = 'tentative',
}

// Guest interface aligned with backend Guest schema
interface Guest {
  _id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  attendanceStatus: RSVPStatus;
  plusOneAllowed?: boolean;
  hasPlusOne?: boolean;
  plusOneGuest?: string;
  mealChoice?: string;
  specialMealRequest?: string;
  notes?: string;
  couple: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Guest Service methods
const GuestService = {
  async getGuests(): Promise<Guest[]> {
    try {
      const response = await api.get<Guest[]>('/guests');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  async getGuestCounts(): Promise<{
    total: number;
    attending: number;
    declined: number;
    pending: number;
    tentative: number;
  }> {
    try {
      const response = await api.get('/guests/counts');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  async createGuest(guestData: Partial<Guest>): Promise<Guest> {
    try {
      const response = await api.post<Guest>('/guests', guestData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  async updateGuest(id: string, guestData: Partial<Guest>): Promise<Guest> {
    try {
      const response = await api.patch<Guest>(`/guests/${id}`, guestData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  async deleteGuest(id: string): Promise<void> {
    try {
      await api.delete(`/guests/${id}`);
    } catch (error) {
      throw this.handleError(error);
    }
  },

  handleError(error: any): Error {
    if (error.response && error.response.data && error.response.data.message) {
      const message = Array.isArray(error.response.data.message)
        ? error.response.data.message.join(', ')
        : error.response.data.message;
      return new Error(message);
    }
    return error instanceof Error ? error : new Error('An unexpected error occurred');
  },
};

const GuestList: React.FC = () => {
  const { currentCouple } = useCouple();

  const [guests, setGuests] = useState<Guest[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [currentGuest, setCurrentGuest] = useState<Guest | null>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [rsvpFilter, setRsvpFilter] = useState<string>('all');
  const [filteredGuests, setFilteredGuests] = useState<Guest[]>([]);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    attending: 0,
    declined: 0,
    pending: 0,
    tentative: 0,
    totalAttendees: 0,
  });

  // Form state
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [attendanceStatus, setAttendanceStatus] = useState<RSVPStatus>(RSVPStatus.PENDING);
  const [plusOneAllowed, setPlusOneAllowed] = useState<boolean>(false);
  const [mealChoice, setMealChoice] = useState<string>('');
  const [specialMealRequest, setSpecialMealRequest] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [formLoading, setFormLoading] = useState<boolean>(false);

  // Map RSVPStatus to stats keys
  const statusToStatsKey: { [key in RSVPStatus]: keyof typeof stats } = {
    [RSVPStatus.ATTENDING]: 'attending',
    [RSVPStatus.DECLINED]: 'declined',
    [RSVPStatus.PENDING]: 'pending',
    [RSVPStatus.TENTATIVE]: 'tentative',
  };

  useEffect(() => {
    if (currentCouple) {
      fetchGuestsAndStats();
    }
  }, [currentCouple]);

  useEffect(() => {
    applyFilters();
  }, [guests, searchQuery, rsvpFilter]);

  const fetchGuestsAndStats = async () => {
    if (!currentCouple) return;

    try {
      setLoading(true);
      const [fetchedGuests, guestCounts] = await Promise.all([
        GuestService.getGuests(),
        GuestService.getGuestCounts(),
      ]);
      setGuests(fetchedGuests);
      const totalAttendees = fetchedGuests
        .filter((guest) => guest.attendanceStatus === RSVPStatus.ATTENDING)
        .reduce((total, guest) => total + (guest.hasPlusOne ? 2 : 1), 0);
      setStats({ ...guestCounts, totalAttendees });
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch guest list';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let result = [...guests];

    // Apply RSVP filter
    if (rsvpFilter !== 'all') {
      result = result.filter((guest) => guest.attendanceStatus === rsvpFilter);
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (guest) =>
          `${guest.firstName} ${guest.lastName}`.toLowerCase().includes(query) ||
          (guest.email && guest.email.toLowerCase().includes(query)) ||
          (guest.phone && guest.phone.toLowerCase().includes(query))
      );
    }

    // Sort by name
    result.sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));

    setFilteredGuests(result);
  };

  const handleAddGuest = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  
    if (!currentCouple) {
      setError("No couple selected. Please create or select a couple profile first.");
      return;
    }
  
    setFormLoading(true);
  
    try {
      const guestData = {
        firstName,
        lastName,
        email: email || undefined,
        phone: phone || undefined,
        attendanceStatus,
        plusOneAllowed,
        mealChoice: mealChoice || undefined,
        specialMealRequest: specialMealRequest || undefined,
        notes: notes || undefined,
        couple: currentCouple._id, // Ensure the couple ID is included
      };
  
      // Log the data being sent for debugging
      console.log('Creating guest with data:', guestData);
      
      const newGuest = await GuestService.createGuest(guestData);
      setGuests((prev) => [...prev, newGuest]);
  
      const statsKey = statusToStatsKey[newGuest.attendanceStatus];
  
      setStats((prev) => ({
        ...prev,
        total: prev.total + 1,
        [statsKey]: prev[statsKey] + 1,
        totalAttendees:
          newGuest.attendanceStatus === RSVPStatus.ATTENDING
            ? prev.totalAttendees + (newGuest.hasPlusOne ? 2 : 1)
            : prev.totalAttendees,
      }));
  
      resetForm();
      setIsAddModalOpen(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add guest';
      setError(errorMessage);
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditGuest = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!currentCouple || !currentGuest) return;

    setFormLoading(true);

    try {
      const guestData = {
        firstName,
        lastName,
        email: email || undefined,
        phone: phone || undefined,
        attendanceStatus,
        plusOneAllowed,
        mealChoice: mealChoice || undefined,
        specialMealRequest: specialMealRequest || undefined,
        notes: notes || undefined,
      };

      const updatedGuest = await GuestService.updateGuest(currentGuest._id, guestData);
      setGuests((prev) =>
        prev.map((guest) => (guest._id === updatedGuest._id ? updatedGuest : guest))
      );

      const oldStatsKey = statusToStatsKey[currentGuest.attendanceStatus];
      const newStatsKey = statusToStatsKey[updatedGuest.attendanceStatus];

      setStats((prev) => ({
        ...prev,
        [oldStatsKey]: prev[oldStatsKey] - 1,
        [newStatsKey]: prev[newStatsKey] + 1,
        totalAttendees:
          updatedGuest.attendanceStatus === RSVPStatus.ATTENDING
            ? prev.totalAttendees +
              (updatedGuest.hasPlusOne ? 2 : 1) -
              (currentGuest.attendanceStatus === RSVPStatus.ATTENDING
                ? currentGuest.hasPlusOne
                  ? 2
                  : 1
                : 0)
            : currentGuest.attendanceStatus === RSVPStatus.ATTENDING
            ? prev.totalAttendees - (currentGuest.hasPlusOne ? 2 : 1)
            : prev.totalAttendees,
      }));

      resetForm();
      setIsEditModalOpen(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update guest';
      setError(errorMessage);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteGuest = async () => {
    if (!currentGuest) return;

    setFormLoading(true);

    try {
      await GuestService.deleteGuest(currentGuest._id);
      const updatedGuests = guests.filter((guest) => guest._id !== currentGuest._id);
      setGuests(updatedGuests);

      const statsKey = statusToStatsKey[currentGuest.attendanceStatus];

      setStats((prev) => ({
        ...prev,
        total: prev.total - 1,
        [statsKey]: prev[statsKey] - 1,
        totalAttendees:
          currentGuest.attendanceStatus === RSVPStatus.ATTENDING
            ? prev.totalAttendees - (currentGuest.hasPlusOne ? 2 : 1)
            : prev.totalAttendees,
      }));

      setIsDeleteModalOpen(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete guest';
      setError(errorMessage);
    } finally {
      setFormLoading(false);
    }
  };

  const openEditModal = (guest: Guest) => {
    setCurrentGuest(guest);
    setFirstName(guest.firstName);
    setLastName(guest.lastName);
    setEmail(guest.email || '');
    setPhone(guest.phone || '');
    setAttendanceStatus(guest.attendanceStatus);
    setPlusOneAllowed(guest.plusOneAllowed || false);
    setMealChoice(guest.mealChoice || '');
    setSpecialMealRequest(guest.specialMealRequest || '');
    setNotes(guest.notes || '');
    setIsEditModalOpen(true);
  };

  const openDeleteModal = (guest: Guest) => {
    setCurrentGuest(guest);
    setIsDeleteModalOpen(true);
  };

  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setAttendanceStatus(RSVPStatus.PENDING);
    setPlusOneAllowed(false);
    setMealChoice('');
    setSpecialMealRequest('');
    setNotes('');
    setCurrentGuest(null);
  };

  const getRsvpStatusClass = (status: RSVPStatus) => {
    switch (status) {
      case RSVPStatus.ATTENDING:
        return 'bg-green-100 text-green-800';
      case RSVPStatus.DECLINED:
        return 'bg-red-100 text-red-800';
      case RSVPStatus.PENDING:
        return 'bg-yellow-100 text-yellow-800';
      case RSVPStatus.TENTATIVE:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const getRsvpStatusIcon = (status: RSVPStatus) => {
    switch (status) {
      case RSVPStatus.ATTENDING:
        return <Check className="h-4 w-4" />;
      case RSVPStatus.DECLINED:
        return <X className="h-4 w-4" />;
      case RSVPStatus.PENDING:
      case RSVPStatus.TENTATIVE:
        return null;
    }
  };

  if (!currentCouple) {
    return (
      <AdminLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-10">
          <p className="text-center text-gray-500">Please create a couple profile first.</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-10">
        <div className="md:flex md:items-center md:justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Guest List</h2>
            <p className="mt-1 text-sm text-gray-500">
              Manage your wedding guests and RSVP status
            </p>
          </div>
          <div className="mt-4 md:mt-0">
            <Button
              onClick={() => {
                resetForm();
                setIsAddModalOpen(true);
              }}
              leftIcon={<Plus className="h-4 w-4" />}
            >
              Add Guest
            </Button>
          </div>
        </div>

        {error && (
          <Alert
            type="error"
            message={error}
            dismissible
            onDismiss={() => setError(null)}
            className="mb-6"
          />
        )}

        {/* Stats */}
        <div className="mb-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Users className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Guests</dt>
                    <dd>
                      <div className="text-lg font-medium text-gray-900">{stats.total}</div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Check className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Attending</dt>
                    <dd>
                      <div className="text-lg font-medium text-gray-900">{stats.attending}</div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <X className="h-6 w-6 text-red-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Declined</dt>
                    <dd>
                      <div className="text-lg font-medium text-gray-900">{stats.declined}</div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Users className="h-6 w-6 text-yellow-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Pending</dt>
                    <dd>
                      <div className="text-lg font-medium text-gray-900">{stats.pending}</div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Users className="h-6 w-6 text-pink-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Attendees</dt>
                    <dd>
                      <div className="text-lg font-medium text-gray-900">{stats.totalAttendees}</div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filter controls */}
        <div className="bg-white shadow rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="search" className="sr-only">
                Search
              </label>
              <div className="relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg
                    className="h-5 w-5 text-gray-400"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  id="search"
                  className="focus:ring-pink-500 focus:border-pink-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                  placeholder="Search guests"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label htmlFor="rsvp-filter" className="sr-only">
                RSVP Status
              </label>
              <select
                id="rsvp-filter"
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm rounded-md"
                value={rsvpFilter}
                onChange={(e) => setRsvpFilter(e.target.value)}
              >
                <option value="all">All RSVP Statuses</option>
                <option value={RSVPStatus.ATTENDING}>Attending</option>
                <option value={RSVPStatus.DECLINED}>Declined</option>
                <option value={RSVPStatus.PENDING}>Pending</option>
                <option value={RSVPStatus.TENTATIVE}>Tentative</option>
              </select>
            </div>
          </div>
        </div>

        {/* Guest List */}
        {loading ? (
          <div className="text-center py-12">
            <Loader size="lg" label="Loading guests..." />
          </div>
        ) : filteredGuests.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-8 text-center">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-lg font-medium text-gray-900">
              {guests.length === 0 ? 'No guests added yet' : 'No guests match your filters'}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {guests.length === 0
                ? 'Start adding guests to your wedding.'
                : 'Try changing your search criteria or filter.'}
            </p>
            {guests.length === 0 && (
              <div className="mt-6">
                <Button
                  onClick={() => {
                    resetForm();
                    setIsAddModalOpen(true);
                  }}
                >
                  Add Your First Guest
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Guest
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Contact
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    RSVP Status
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Party Size
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Meal Preference
                  </th>
                  <th scope="col" className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredGuests.map((guest) => (
                  <tr key={guest._id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{`${guest.firstName} ${guest.lastName}`}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        {guest.email && (
                          <div className="flex items-center text-sm text-gray-500">
                            <Mail className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                            <span>{guest.email}</span>
                          </div>
                        )}
                        {guest.phone && (
                          <div className="flex items-center text-sm text-gray-500 mt-1">
                            <Phone className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                            <span>{guest.phone}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRsvpStatusClass(
                          guest.attendanceStatus
                        )}`}
                      >
                        {getRsvpStatusIcon(guest.attendanceStatus)}
                        <span className={getRsvpStatusIcon(guest.attendanceStatus) ? 'ml-1' : ''}>
                          {guest.attendanceStatus === RSVPStatus.ATTENDING
                            ? 'Attending'
                            : guest.attendanceStatus === RSVPStatus.DECLINED
                            ? 'Declined'
                            : guest.attendanceStatus === RSVPStatus.PENDING
                            ? 'Pending'
                            : 'Tentative'}
                        </span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {guest.hasPlusOne ? 2 : 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {guest.mealChoice || guest.specialMealRequest || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => openEditModal(guest)}
                          className="text-pink-600 hover:text-pink-900"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => openDeleteModal(guest)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Add Guest Modal */}
        <Modal open={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title="Add Guest">
          <form onSubmit={handleAddGuest} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                id="guest-firstName"
                label="First Name"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="e.g. John"
              />
              <Input
                id="guest-lastName"
                label="Last Name"
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="e.g. Smith"
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                id="guest-email"
                label="Email (optional)"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="e.g. john@example.com"
              />
              <Input
                id="guest-phone"
                label="Phone (optional)"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="e.g. (123) 456-7890"
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="rsvp-status" className="block text-sm font-medium text-gray-700">
                  RSVP Status
                </label>
                <select
                  id="rsvp-status"
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm rounded-md"
                  value={attendanceStatus}
                  onChange={(e) => setAttendanceStatus(e.target.value as RSVPStatus)}
                >
                  <option value={RSVPStatus.ATTENDING}>Attending</option>
                  <option value={RSVPStatus.DECLINED}>Declined</option>
                  <option value={RSVPStatus.PENDING}>Pending</option>
                  <option value={RSVPStatus.TENTATIVE}>Tentative</option>
                </select>
              </div>
              <div>
                <label
                  htmlFor="plus-one-allowed"
                  className="block text-sm font-medium text-gray-700"
                >
                  Allow Plus One
                </label>
                <input
                  id="plus-one-allowed"
                  type="checkbox"
                  checked={plusOneAllowed}
                  onChange={(e) => setPlusOneAllowed(e.target.checked)}
                  className="mt-2 h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                id="guest-meal"
                label="Meal Choice (optional)"
                value={mealChoice}
                onChange={(e) => setMealChoice(e.target.value)}
                placeholder="e.g. CHICKEN, BEEF, VEGETARIAN"
              />
              <Input
                id="guest-special-meal"
                label="Special Meal Request (optional)"
                value={specialMealRequest}
                onChange={(e) => setSpecialMealRequest(e.target.value)}
                placeholder="e.g. Gluten-free"
              />
            </div>

            <Textarea
              id="guest-notes"
              label="Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any special notes or requests"
            />

            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={() => setIsAddModalOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={formLoading}
                disabled={formLoading || !firstName || !lastName}
              >
                Add Guest
              </Button>
            </div>
          </form>
        </Modal>

        {/* Edit Guest Modal */}
        <Modal open={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title="Edit Guest">
          <form onSubmit={handleEditGuest} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                id="edit-guest-firstName"
                label="First Name"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="e.g. John"
              />
              <Input
                id="edit-guest-lastName"
                label="Last Name"
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="e.g. Smith"
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                id="edit-guest-email"
                label="Email (optional)"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="e.g. john@example.com"
              />
              <Input
                id="edit-guest-phone"
                label="Phone (optional)"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="e.g. (123) 456-7890"
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="edit-rsvp-status"
                  className="block text-sm font-medium text-gray-700"
                >
                  RSVP Status
                </label>
                <select
                  id="edit-rsvp-status"
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm rounded-md"
                  value={attendanceStatus}
                  onChange={(e) => setAttendanceStatus(e.target.value as RSVPStatus)}
                >
                  <option value={RSVPStatus.ATTENDING}>Attending</option>
                  <option value={RSVPStatus.DECLINED}>Declined</option>
                  <option value={RSVPStatus.PENDING}>Pending</option>
                  <option value={RSVPStatus.TENTATIVE}>Tentative</option>
                </select>
              </div>
              <div>
                <label
                  htmlFor="edit-plus-one-allowed"
                  className="block text-sm font-medium text-gray-700"
                >
                  Allow Plus One
                </label>
                <input
                  id="edit-plus-one-allowed"
                  type="checkbox"
                  checked={plusOneAllowed}
                  onChange={(e) => setPlusOneAllowed(e.target.checked)}
                  className="mt-2 h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                id="edit-guest-meal"
                label="Meal Choice (optional)"
                value={mealChoice}
                onChange={(e) => setMealChoice(e.target.value)}
                placeholder="e.g. CHICKEN, BEEF, VEGETARIAN"
              />
              <Input
                id="edit-guest-special-meal"
                label="Special Meal Request (optional)"
                value={specialMealRequest}
                onChange={(e) => setSpecialMealRequest(e.target.value)}
                placeholder="e.g. Gluten-free"
              />
            </div>

            <Textarea
              id="edit-guest-notes"
              label="Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any special notes or requests"
            />

            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={formLoading}
                disabled={formLoading || !firstName || !lastName}
              >
                Save Changes
              </Button>
            </div>
          </form>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          open={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          title="Delete Guest"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              Are you sure you want to delete {currentGuest?.firstName} {currentGuest?.lastName} from
              your guest list? This action cannot be undone.
            </p>

            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={() => setIsDeleteModalOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteGuest}
                loading={formLoading}
                disabled={formLoading}
              >
                Delete Guest
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </AdminLayout>
  );
};

export default GuestList;